import React, { useState, useEffect } from "react";
import { Modal, Tab, Tabs } from "react-bootstrap";
import {
  useMarketingPlan,
  MarketingPlan,
  MarketingChannel,
  Campaign,
  ContentIdea,
} from "../../../../hooks/useMarketingPlan";

interface Props {
  show: boolean;
  handleClose: () => void;
  projectId: string;
  projectTitle: string;
}

export const MarketingPlanModal: React.FC<Props> = ({
  show,
  handleClose,
  projectId,
  projectTitle,
}) => {
  const [activeTab, setActiveTab] = useState("generate");
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [selectedContentType, setSelectedContentType] = useState<string>("social");
  const [generatingContent, setGeneratingContent] = useState<string | null>(null);
  const [contentResults, setContentResults] = useState<Record<string, string>>({});

  const {
    isLoading,
    isGeneratingImage,
    isGeneratingContent,
    error,
    plan,
    generatedImage,
    generatePlan,
    generateImage,
    generateContent,
    clearError,
    reset,
  } = useMarketingPlan();

  useEffect(() => {
    if (show) {
      reset();
      setActiveTab("generate");
      setSelectedChannel(null);
      setContentResults({});
    }
  }, [show, reset]);

  const handleGenerate = async () => {
    await generatePlan(projectId);
    setActiveTab("overview");
  };

  const handleGenerateContent = async (type: string, platform?: string) => {
    setGeneratingContent(type);
    const result = await generateContent(projectId, type, platform);
    if (result) {
      setContentResults((prev) => ({
        ...prev,
        [`${type}-${platform || "general"}`]: result,
      }));
    }
    setGeneratingContent(null);
  };

  const handleGenerateImage = async (imageType: string) => {
    await generateImage(projectId, imageType);
  };

  const getPriorityBadge = (priority: "high" | "medium" | "low") => {
    const colors = {
      high: "success",
      medium: "warning",
      low: "secondary",
    };
    return (
      <span className={`badge bg-${colors[priority]} text-uppercase`}>
        {priority}
      </span>
    );
  };

  const getObjectiveBadge = (objective: "awareness" | "engagement" | "conversion") => {
    const colors = {
      awareness: "info",
      engagement: "primary",
      conversion: "success",
    };
    const icons = {
      awareness: "bi-eye",
      engagement: "bi-heart",
      conversion: "bi-cart",
    };
    return (
      <span className={`badge bg-${colors[objective]}`}>
        <i className={`bi ${icons[objective]} me-1`}></i>
        {objective}
      </span>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      backdrop="static"
      scrollable
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bolder">
          <i className="bi bi-megaphone me-2 text-primary"></i>
          Marketing Plan Generator
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-3">
        {error && (
          <div className="alert alert-danger mb-4 d-flex align-items-center">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <span className="flex-grow-1">{error}</span>
            <button
              type="button"
              className="btn-close"
              onClick={clearError}
            ></button>
          </div>
        )}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => k && setActiveTab(k)}
          className="mb-4"
        >
          {/* Generate Tab */}
          <Tab eventKey="generate" title="Generate">
            <div className="py-4 text-center">
              <div className="mb-5">
                <div
                  className="d-inline-flex align-items-center justify-content-center bg-light-primary rounded-circle mb-4"
                  style={{ width: "100px", height: "100px" }}
                >
                  <i className="bi bi-megaphone text-primary fs-1"></i>
                </div>
                <h3 className="fw-bold mb-2">AI Marketing Plan</h3>
                <p className="text-muted mx-auto" style={{ maxWidth: "500px" }}>
                  Generate a comprehensive marketing strategy for{" "}
                  <strong>{projectTitle}</strong> based on your project data,
                  branding, and target audience.
                </p>
              </div>

              <div className="bg-light-primary rounded p-4 mb-5 mx-auto" style={{ maxWidth: "600px" }}>
                <h6 className="fw-bold text-primary mb-3">
                  <i className="bi bi-magic me-2"></i>
                  What will be generated
                </h6>
                <div className="row text-start">
                  <div className="col-md-6">
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Channel strategy & priorities
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        12-week marketing calendar
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Campaign ideas
                      </li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        SEO keywords & strategy
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Social media profiles
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Content ideas
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <button
                className="btn btn-primary btn-lg px-5"
                onClick={handleGenerate}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Generating Marketing Plan...
                  </>
                ) : (
                  <>
                    <i className="bi bi-magic me-2"></i>
                    Generate Marketing Plan
                  </>
                )}
              </button>
            </div>
          </Tab>

          {/* Overview Tab */}
          <Tab eventKey="overview" title="Overview" disabled={!plan}>
            {plan && (
              <div className="py-4">
                <div className="row g-4">
                  <div className="col-md-4">
                    <div className="card h-100 border-0 bg-light-primary">
                      <div className="card-body">
                        <h6 className="fw-bold text-primary mb-3">
                          <i className="bi bi-bullseye me-2"></i>
                          Mission
                        </h6>
                        <p className="mb-0 text-gray-700">{plan.overview.mission}</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card h-100 border-0 bg-light-success">
                      <div className="card-body">
                        <h6 className="fw-bold text-success mb-3">
                          <i className="bi bi-people me-2"></i>
                          Target Audience
                        </h6>
                        <p className="mb-0 text-gray-700">{plan.overview.targetAudience}</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card h-100 border-0 bg-light-warning">
                      <div className="card-body">
                        <h6 className="fw-bold text-warning mb-3">
                          <i className="bi bi-star me-2"></i>
                          Unique Value
                        </h6>
                        <p className="mb-0 text-gray-700">{plan.overview.uniqueValue}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="row g-4 mt-2">
                  <div className="col-md-3">
                    <div className="card border-0 bg-light text-center py-3">
                      <div className="fs-2 fw-bold text-primary">{plan.channels.length}</div>
                      <div className="text-muted fs-7">Channels</div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card border-0 bg-light text-center py-3">
                      <div className="fs-2 fw-bold text-success">{plan.calendar.length}</div>
                      <div className="text-muted fs-7">Weeks Planned</div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card border-0 bg-light text-center py-3">
                      <div className="fs-2 fw-bold text-warning">{plan.campaigns.length}</div>
                      <div className="text-muted fs-7">Campaigns</div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card border-0 bg-light text-center py-3">
                      <div className="fs-2 fw-bold text-info">{plan.contentIdeas.length}</div>
                      <div className="text-muted fs-7">Content Ideas</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Tab>

          {/* Channels Tab */}
          <Tab eventKey="channels" title="Channels" disabled={!plan}>
            {plan && (
              <div className="py-4">
                <h5 className="fw-bold mb-4">Recommended Marketing Channels</h5>
                <div className="row g-4">
                  {plan.channels.map((channel, index) => (
                    <div key={index} className="col-md-6">
                      <div
                        className={`card h-100 cursor-pointer ${
                          selectedChannel === channel.name
                            ? "border-primary"
                            : "border-gray-300"
                        }`}
                        onClick={() =>
                          setSelectedChannel(
                            selectedChannel === channel.name ? null : channel.name
                          )
                        }
                      >
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="d-flex align-items-center">
                              <ChannelIcon name={channel.name} />
                              <h6 className="fw-bold mb-0 ms-2">{channel.name}</h6>
                            </div>
                            {getPriorityBadge(channel.priority)}
                          </div>

                          <div className="mb-3">
                            <div className="d-flex justify-content-between mb-1">
                              <span className="text-muted fs-7">Audience Match</span>
                              <span className="fw-bold fs-7">{channel.audienceMatch}%</span>
                            </div>
                            <div className="progress" style={{ height: "6px" }}>
                              <div
                                className="progress-bar bg-primary"
                                style={{ width: `${channel.audienceMatch}%` }}
                              ></div>
                            </div>
                          </div>

                          <p className="text-muted fs-7 mb-3">{channel.reasoning}</p>

                          <div className="mb-2">
                            <span className="text-muted fs-7">Posting:</span>
                            <span className="ms-2 fs-7">{channel.postingFrequency}</span>
                          </div>

                          <div className="d-flex flex-wrap gap-1">
                            {channel.contentTypes.map((type, i) => (
                              <span key={i} className="badge bg-light text-dark fs-8">
                                {type}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Tab>

          {/* Calendar Tab */}
          <Tab eventKey="calendar" title="Calendar" disabled={!plan}>
            {plan && (
              <div className="py-4">
                <h5 className="fw-bold mb-4">12-Week Marketing Calendar</h5>
                <div className="table-responsive">
                  <table className="table table-row-bordered">
                    <thead>
                      <tr className="fw-bold fs-7 text-uppercase text-muted">
                        <th style={{ width: "80px" }}>Week</th>
                        <th style={{ width: "150px" }}>Theme</th>
                        <th>Activities</th>
                        <th style={{ width: "200px" }}>Milestone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.calendar.map((week, index) => (
                        <tr key={index}>
                          <td>
                            <span className="badge bg-primary">Week {week.week}</span>
                          </td>
                          <td className="fw-bold">{week.theme}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-2">
                              {week.activities.map((activity, i) => (
                                <div
                                  key={i}
                                  className="bg-light rounded p-2 fs-7"
                                  style={{ maxWidth: "200px" }}
                                >
                                  <div className="fw-bold">{activity.day}</div>
                                  <div className="text-muted">{activity.task}</div>
                                  <span className="badge bg-light-primary text-primary fs-8">
                                    {activity.channel}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td>
                            {week.milestone && (
                              <span className="text-success">
                                <i className="bi bi-flag-fill me-1"></i>
                                {week.milestone}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Tab>

          {/* Campaigns Tab */}
          <Tab eventKey="campaigns" title="Campaigns" disabled={!plan}>
            {plan && (
              <div className="py-4">
                <h5 className="fw-bold mb-4">Suggested Campaigns</h5>
                <div className="row g-4">
                  {plan.campaigns.map((campaign, index) => (
                    <div key={index} className="col-md-6">
                      <div className="card h-100">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <h6 className="fw-bold mb-0">{campaign.name}</h6>
                            {getObjectiveBadge(campaign.objective)}
                          </div>

                          <p className="text-muted fs-7 mb-3">{campaign.description}</p>

                          <div className="mb-3">
                            <span className="text-muted fs-7">Duration:</span>
                            <span className="ms-2 fs-7">{campaign.duration}</span>
                          </div>

                          <div className="mb-3">
                            <span className="text-muted fs-7 d-block mb-2">Channels:</span>
                            <div className="d-flex flex-wrap gap-1">
                              {campaign.channels.map((ch, i) => (
                                <span key={i} className="badge bg-light-primary text-primary">
                                  {ch}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div>
                            <span className="text-muted fs-7 d-block mb-2">Key Messages:</span>
                            <ul className="list-unstyled mb-0">
                              {campaign.keyMessages.map((msg, i) => (
                                <li key={i} className="fs-7 mb-1">
                                  <i className="bi bi-chat-quote text-primary me-2"></i>
                                  {msg}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Tab>

          {/* SEO Tab */}
          <Tab eventKey="seo" title="SEO" disabled={!plan}>
            {plan && (
              <div className="py-4">
                <h5 className="fw-bold mb-4">SEO Strategy</h5>
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="card h-100">
                      <div className="card-body">
                        <h6 className="fw-bold text-primary mb-3">
                          <i className="bi bi-key me-2"></i>
                          Primary Keywords
                        </h6>
                        <div className="d-flex flex-wrap gap-2">
                          {plan.seoStrategy.primaryKeywords.map((kw, i) => (
                            <span key={i} className="badge bg-primary fs-7 py-2 px-3">
                              {kw}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card h-100">
                      <div className="card-body">
                        <h6 className="fw-bold text-success mb-3">
                          <i className="bi bi-search me-2"></i>
                          Long-Tail Keywords
                        </h6>
                        <div className="d-flex flex-wrap gap-2">
                          {plan.seoStrategy.longTailKeywords.map((kw, i) => (
                            <span key={i} className="badge bg-light-success text-success fs-7 py-2 px-3">
                              {kw}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="card">
                      <div className="card-body">
                        <h6 className="fw-bold text-warning mb-3">
                          <i className="bi bi-diagram-3 me-2"></i>
                          Content Pillars
                        </h6>
                        <div className="row g-3">
                          {plan.seoStrategy.contentPillars.map((pillar, i) => (
                            <div key={i} className="col-md-4">
                              <div className="bg-light-warning rounded p-3 text-center">
                                <i className="bi bi-box fs-4 text-warning d-block mb-2"></i>
                                <span className="fw-bold">{pillar}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Tab>

          {/* Social Profiles Tab */}
          <Tab eventKey="social" title="Social Profiles" disabled={!plan}>
            {plan && (
              <div className="py-4">
                <h5 className="fw-bold mb-4">Social Media Profiles</h5>
                <div className="row g-4">
                  {Object.entries(plan.socialProfiles).map(([platform, profile]) => {
                    if (!profile) return null;
                    return (
                      <div key={platform} className="col-md-6">
                        <div className="card h-100">
                          <div className="card-body">
                            <div className="d-flex align-items-center mb-3">
                              <ChannelIcon name={platform} />
                              <h6 className="fw-bold mb-0 ms-2 text-capitalize">{platform}</h6>
                            </div>

                            {profile.bio && (
                              <div className="mb-3">
                                <label className="text-muted fs-7 d-block mb-1">Bio</label>
                                <div className="bg-light rounded p-2 fs-7">{profile.bio}</div>
                              </div>
                            )}

                            {profile.headline && (
                              <div className="mb-3">
                                <label className="text-muted fs-7 d-block mb-1">Headline</label>
                                <div className="bg-light rounded p-2 fs-7">{profile.headline}</div>
                              </div>
                            )}

                            {profile.contentThemes && (
                              <div className="mb-3">
                                <label className="text-muted fs-7 d-block mb-1">Content Themes</label>
                                <div className="d-flex flex-wrap gap-1">
                                  {profile.contentThemes.map((theme, i) => (
                                    <span key={i} className="badge bg-light text-dark">
                                      {theme}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {profile.hashtagStrategy && (
                              <div>
                                <label className="text-muted fs-7 d-block mb-1">Hashtags</label>
                                <div className="d-flex flex-wrap gap-1">
                                  {profile.hashtagStrategy.map((tag, i) => (
                                    <span key={i} className="badge bg-light-primary text-primary">
                                      #{tag}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Image Generation Section */}
                <div className="mt-5">
                  <h5 className="fw-bold mb-4">Generate Social Media Images</h5>
                  <div className="row g-3">
                    {[
                      { type: "profile", label: "Profile Picture", icon: "bi-person-circle" },
                      { type: "cover", label: "Cover/Banner", icon: "bi-image" },
                      { type: "post", label: "Post Template", icon: "bi-card-image" },
                    ].map((item) => (
                      <div key={item.type} className="col-md-4">
                        <button
                          className="btn btn-light-primary w-100 py-4"
                          onClick={() => handleGenerateImage(item.type)}
                          disabled={isGeneratingImage}
                        >
                          {isGeneratingImage ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            <>
                              <i className={`bi ${item.icon} fs-1 d-block mb-2`}></i>
                              <span className="fw-bold">{item.label}</span>
                            </>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>

                  {generatedImage && (
                    <div className="mt-4">
                      <div className="card">
                        <div className="card-body text-center">
                          <img
                            src={generatedImage}
                            alt="Generated social media image"
                            className="img-fluid rounded mb-3"
                            style={{ maxHeight: "300px" }}
                          />
                          <a
                            href={generatedImage}
                            download
                            className="btn btn-primary"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="bi bi-download me-2"></i>
                            Download Image
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Tab>

          {/* Content Tab */}
          <Tab eventKey="content" title="Content" disabled={!plan}>
            {plan && (
              <div className="py-4">
                <h5 className="fw-bold mb-4">Content Ideas</h5>

                {/* Content Type Filter */}
                <div className="d-flex gap-2 mb-4">
                  {["all", "blog", "social", "video", "email"].map((type) => (
                    <button
                      key={type}
                      className={`btn btn-sm ${
                        selectedContentType === type
                          ? "btn-primary"
                          : "btn-light"
                      }`}
                      onClick={() => setSelectedContentType(type)}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>

                <div className="row g-4">
                  {plan.contentIdeas
                    .filter(
                      (idea) =>
                        selectedContentType === "all" ||
                        idea.type === selectedContentType
                    )
                    .map((idea, index) => (
                      <div key={index} className="col-md-6">
                        <div className="card h-100">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <ContentTypeIcon type={idea.type} />
                              {idea.platform && (
                                <span className="badge bg-light text-dark">
                                  {idea.platform}
                                </span>
                              )}
                            </div>

                            {idea.title && (
                              <h6 className="fw-bold mb-2">{idea.title}</h6>
                            )}

                            {idea.concept && (
                              <p className="text-muted fs-7 mb-3">{idea.concept}</p>
                            )}

                            {idea.format && (
                              <div className="mb-3">
                                <span className="text-muted fs-7">Format:</span>
                                <span className="ms-2 fs-7">{idea.format}</span>
                              </div>
                            )}

                            <button
                              className="btn btn-sm btn-light-primary"
                              onClick={() =>
                                handleGenerateContent(idea.type, idea.platform)
                              }
                              disabled={isGeneratingContent}
                            >
                              {generatingContent === idea.type ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2"></span>
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-magic me-2"></i>
                                  Generate Content
                                </>
                              )}
                            </button>

                            {contentResults[`${idea.type}-${idea.platform || "general"}`] && (
                              <div className="mt-3 bg-light rounded p-3">
                                <label className="text-muted fs-7 d-block mb-2">
                                  Generated Content:
                                </label>
                                <div className="fs-7 text-gray-700" style={{ whiteSpace: "pre-wrap" }}>
                                  {contentResults[`${idea.type}-${idea.platform || "general"}`]}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <button
          type="button"
          className="btn btn-light"
          onClick={handleClose}
          disabled={isLoading}
        >
          {plan ? "Done" : "Cancel"}
        </button>

        {plan && activeTab !== "generate" && (
          <button
            type="button"
            className="btn btn-light-primary"
            onClick={() => {
              reset();
              setActiveTab("generate");
            }}
          >
            <i className="bi bi-arrow-clockwise me-2"></i>
            Regenerate Plan
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

// Helper component for channel icons
const ChannelIcon: React.FC<{ name: string }> = ({ name }) => {
  const icons: Record<string, string> = {
    instagram: "bi-instagram",
    linkedin: "bi-linkedin",
    twitter: "bi-twitter-x",
    facebook: "bi-facebook",
    tiktok: "bi-tiktok",
    youtube: "bi-youtube",
    pinterest: "bi-pinterest",
    email: "bi-envelope",
    blog: "bi-journal-text",
  };

  const colors: Record<string, string> = {
    instagram: "#E4405F",
    linkedin: "#0A66C2",
    twitter: "#000000",
    facebook: "#1877F2",
    tiktok: "#000000",
    youtube: "#FF0000",
    pinterest: "#BD081C",
    email: "#6c757d",
    blog: "#6c757d",
  };

  const lowerName = name.toLowerCase();
  const icon = icons[lowerName] || "bi-globe";
  const color = colors[lowerName] || "#6c757d";

  return (
    <div
      className="d-flex align-items-center justify-content-center rounded"
      style={{
        width: "32px",
        height: "32px",
        backgroundColor: `${color}20`,
      }}
    >
      <i className={`bi ${icon}`} style={{ color }}></i>
    </div>
  );
};

// Helper component for content type icons
const ContentTypeIcon: React.FC<{ type: string }> = ({ type }) => {
  const defaultConfig = { icon: "bi-share", color: "info", label: "Social Media" };
  const config: Record<string, { icon: string; color: string; label: string }> = {
    blog: { icon: "bi-journal-text", color: "primary", label: "Blog Post" },
    social: defaultConfig,
    video: { icon: "bi-play-circle", color: "danger", label: "Video" },
    email: { icon: "bi-envelope", color: "warning", label: "Email" },
  };

  const { icon, color, label } = config[type] || defaultConfig;

  return (
    <span className={`badge bg-light-${color} text-${color}`}>
      <i className={`bi ${icon} me-1`}></i>
      {label}
    </span>
  );
};

export default MarketingPlanModal;
